import { ApiError } from '../../../api/client';
import type { PlanName } from '../../../api/types';

// PARA-132 (AD-3) — единственное место, где статус ответа превращается в текст.
// Строка detail НЕ парсится (она локализуема и меняется без предупреждения):
// ветвление идёт по status, body.reason и локальному состоянию.

/** Совпадает с MAX_AUDIO_SIZE_BYTES backend (transcribe_voice_input.py): 10 МБ. */
export const MAX_AUDIO_SIZE_BYTES = 10 * 1024 * 1024;

export interface VoiceFailure {
  /** Не только текст, но и реакция экрана: переход в биллинг / модалка согласия. */
  kind: 'plan' | 'consent' | 'message';
  /** Ключ в namespace 'voice'. */
  messageKey: string;
}

/** AC-E11a — клиентская отсечка по размеру до отправки: текст тот же, что у
 * backend-ответа 413 reason: 'size'. */
export const AUDIO_TOO_LARGE_FAILURE: VoiceFailure = {
  kind: 'message',
  messageKey: 'errors.tooLarge',
};

const GENERIC_FAILURE: VoiceFailure = { kind: 'message', messageKey: 'errors.generic' };

interface VoiceErrorContext {
  /** План из useBillingStatus(); undefined = ещё неизвестен. */
  plan?: PlanName;
  hasConsent: boolean;
}

/** Отказ в доступе к микрофону приходит как DOMException, но проверять класс
 * ненадёжно (разные реализации WebView) — различаем по имени. */
function isPermissionDenied(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { name?: unknown }).name === 'NotAllowedError'
  );
}

/** reason из тела 413 (`{ detail, reason }`, B-5). */
function audioLimitReason(body: unknown): string | null {
  if (typeof body !== 'object' || body === null) {
    return null;
  }
  const reason = (body as { reason?: unknown }).reason;
  return typeof reason === 'string' ? reason : null;
}

/** Порядок ветвления 403 повторяет порядок проверок backend «тариф → consent»
 * (B-8): иначе тексты разошлись бы с реальностью. */
function mapForbidden({ plan, hasConsent }: VoiceErrorContext): VoiceFailure {
  const planFailure: VoiceFailure = { kind: 'plan', messageKey: 'errors.plan' };
  if (plan !== undefined && plan !== 'ULTIMATE') {
    return planFailure;
  }
  if (!hasConsent) {
    return { kind: 'consent', messageKey: 'errors.consent' };
  }
  if (plan === undefined) {
    // План не удалось загрузить (fail-open по AC-16a), но согласие выдано —
    // из двух проверок backend остаётся только тариф: показываем текст про
    // тариф и путь в биллинг вместо бесполезного generic.
    return planFailure;
  }
  // EC-16: полный рассинхрон клиента и backend — generic, но всё так же без
  // разлогина и без потери введённого текста.
  return GENERIC_FAILURE;
}

export function mapVoiceError(error: unknown, context: VoiceErrorContext): VoiceFailure {
  if (isPermissionDenied(error)) {
    return { kind: 'message', messageKey: 'errors.micDenied' };
  }
  if (!(error instanceof ApiError)) {
    // Сетевая ошибка (fetch reject) и всё неопознанное — AC-E10, AC-E13.
    return GENERIC_FAILURE;
  }
  switch (error.status) {
    case 403:
      return mapForbidden(context);
    case 413: {
      const reason = audioLimitReason(error.body);
      if (reason === 'size') {
        return AUDIO_TOO_LARGE_FAILURE;
      }
      if (reason === 'duration') {
        return { kind: 'message', messageKey: 'errors.tooLong' };
      }
      return GENERIC_FAILURE;
    }
    case 422:
      return { kind: 'message', messageKey: 'errors.notRecognized' };
    case 429:
      return { kind: 'message', messageKey: 'errors.rateLimit' };
    case 503:
      return { kind: 'message', messageKey: 'errors.unavailable' };
    default:
      return GENERIC_FAILURE;
  }
}
